// @flow
import * as React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import type {
	TPosition,
	TSquad,
	TPlayerStatusesReducer
} from '../../modules/types';

import colors from '../../assets/css/colors';
import Search from '../../components/Icons/Search';
import SortDown from '../../components/Icons/SortDown';
import SortUp from '../../components/Icons/SortUp';
import ArrowUp from '../../components/Icons/ArrowUp';
import ArrowDown from '../../components/Icons/ArrowDown';
import { Select } from '../Form';
import {
	SearchField,
	SearchSubmitButton,
	SearchInputWrapper,
	SwitchToggle,
} from "../../components";
import { below } from "../../assets/css/media";

const DraftFiltersForm = styled.form`
	margin: 1em 0 0;

	${below.desktop`
		padding: 0 15px;
	`};


`;

const SearchWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	margin: 1em 0 0;
`;

const SelectWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 1em;

	> div {
		flex-basis: 32.5%;
	}

	${below.tablet`
		flex-wrap: wrap;
		> div {
			flex-basis: 100%;
			margin-bottom: 10px;
		}
	`};
`;

const CheckboxLabel = styled.label`
	cursor: pointer;
	height: 36px;
	width: 36px;
	border: 1px solid ${colors.nrlDarkGreen};
	background-color: #FFFFFF;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-left: 1em;
	svg {
		margin: 0 auto;
	}
`;

const HiddenCheckbox = styled.input`
	display: none;
`;

const ToggleButton = styled.button`
	border: none;
	outline: 0;
	background: rgba(0,0,0,0);
	width: 100%;
	height: 50px;
	font-size: 12px;
	font-weight: bold;
	letter-spacing: 0.5px;
	line-height: 1;
	text-align: center;
	color: ${colors.darkGrey};
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	span {
		margin-right: .5em;
	}
`;

const SwitchToggleGroup = styled.div`
	display: flex;
	align-items: center;
	margin: 20px 0 9px;

	${below.tablet`
		flex-wrap: wrap;
		margin: 10px 0 0;

		> div {
			flex-basis: 100%;
			margin-bottom: 10px;
			margin-right: 0;
		}
	`};
`;

type Props = {
	handleChange: Function,
	handleSubmit: Function,
	onToggleOrder: Function,
	order_by_desc?: boolean,
	is_show_drafted?: boolean,
	is_show_ignored?: boolean,
	positions: Array<TPosition>,
	squads: Array<TSquad>,
	statuses: TPlayerStatusesReducer,
	is_hide_sort?: boolean,
	is_show_owner?: boolean,
	hide_rfa?: boolean,
	selected_by_position?: string,
};

type State = {
	more_filters: boolean,
	by_dpp: boolean,
	by_favourites: boolean,
	by_drafted: boolean,
	by_ignored: boolean,
};

export class DraftFilters extends React.Component<Props, State> {

	state = {
		more_filters: false,
		by_dpp: false,
		by_favourites: false,
		by_drafted: false,
		by_ignored: false
	};

	static defaultProps = {
		order_by: true,
		is_show_drafted: false,
		is_hide_sort: true,
		is_show_owner:false,
		hide_rfa:true,
	};

	constructor(props: Props, state: State) {
		super(props, state);
		_.bindAll(this, [
			'toggleFilters',
			'handleSwitchChange',
		]);
	}

	toggleFilters() {
		const { more_filters } = this.state;
		this.setState({ more_filters: !more_filters });
	}

	handleSwitchChange({ currentTarget }: Object) {
		const { name, checked } = currentTarget;
		this.setState({ [name]: checked });
	}
	get owner_filter() {
		const {
			is_show_owner,
			hide_rfa
		} = this.props;

		if (!is_show_owner){
			return null;
		}

		return (
			<div>
				<Select is_disabled={false} name="by_owner">
					<option value="fa_rfa">
								Available Players
					</option>
					<option value="">
								All Players
					</option>
					{hide_rfa ? null : (
						<option value="rfa">
							Restricted Free Agents
						</option>
					)}
					<option value="owned">
								Owned Players
					</option>
				</Select>
			</div>
		);
	}

	renderMoreLessToggle(more_filters: boolean) {
		return (
			<ToggleButton onClick={this.toggleFilters}>
				{more_filters? (
					<React.Fragment>
						<span>LESS FILTERS</span> <ArrowUp />
					</React.Fragment>
				): (
					<React.Fragment>
						<span>MORE FILTERS</span> <ArrowDown />
					</React.Fragment>
				)}
			</ToggleButton>
		);
	}

	render() {
		const {
			handleChange,
			handleSubmit,
			order_by_desc,
			onToggleOrder,
			is_show_drafted,
			is_show_ignored,
			positions,
			squads,
			statuses,
			is_hide_sort,
			selected_by_position,
		} = this.props;
		const {
			more_filters,
			by_dpp,
			by_favourites,
			by_drafted,
			by_ignored
		} = this.state;

		return (
			<DraftFiltersForm onChange={handleChange} onSubmit={handleSubmit}>
				<SearchWrapper>
					<SearchInputWrapper>
						<SearchField
							type="text"
							name="search"
							component="input"
							placeholder="Search for a player"
						/>
						<SearchSubmitButton type="submit">
							<Search />
						</SearchSubmitButton>
					</SearchInputWrapper>
					{!is_hide_sort ? (
						<div>
							<CheckboxLabel htmlFor="order_by_desc" onClick={onToggleOrder}>
								{order_by_desc ? <SortDown /> : <SortUp />}
							</CheckboxLabel>
							<HiddenCheckbox
								type="checkbox"
								defaultChecked={order_by_desc}
								value={order_by_desc ? 'desc' : 'asc'}
								id="order_by_desc"
								name="order_by_desc" />
						</div>
					) : null}
				</SearchWrapper>
				<div style={{ display: more_filters? 'block': 'none' }}>
					<SelectWrapper>
						<div>
							<Select name="by_squad" is_disabled={false}>
								<option value="">
										ALL CLUBS
								</option>
								{squads.map(({ id, full_name }) => (
									<option key={id} value={id}>{full_name}</option>
								))}
							</Select>
						</div>
						<div>
							<Select
								readOnly
								value={selected_by_position}
								is_disabled={false} name="by_position"
							>
								<option value="">
										ALL POSITIONS
								</option>
								{positions.map(({ id, full_name }) => (
									<option key={id} value={id}>{full_name}</option>
								))}
							</Select>
						</div>
						<div>
							<Select is_disabled={false} name="by_status">
								<option value="">
										ALL STATUSES
								</option>
								{statuses.map(({ key, value }) => (
									<option key={key} value={key}>{value}</option>
								))}
							</Select>
						</div>
						{this.owner_filter}
					</SelectWrapper>

					<SwitchToggleGroup>
						<SwitchToggle
							id="dpp"
							name="by_dpp"
							checked={by_dpp}
							onChange={this.handleSwitchChange}
							label="Show Dual Position players only"
						/>
						<SwitchToggle
							id="favourites"
							name="by_favourites"
							checked={by_favourites}
							onChange={this.handleSwitchChange}
							label="Show my Favourites"
						/>
						{is_show_drafted && (
							<SwitchToggle
								id="drafted"
								name="by_drafted"
								checked={by_drafted}
								onChange={this.handleSwitchChange}
								label="Show Drafted"
							/>
						)}
						{is_show_ignored && (
							<SwitchToggle
								id="ignored"
								name="by_ignored"
								checked={by_ignored}
								onChange={this.handleSwitchChange}
								label="Show Ignored"
							/>
						)}
					</SwitchToggleGroup>
				</div>
				{this.renderMoreLessToggle(more_filters)}
			</DraftFiltersForm>
		);
	}
}

export default DraftFilters;


// WEBPACK FOOTER //
// ./src/components/DraftFilters/index.js